import type { IJsonModel } from 'flexlayout-react'

/**
 * Tab visibility priority. When a tabset contains multiple tabs, the one with
 * the lowest priority number wins — i.e. 1 is highest priority. Tabs not in
 * this map are treated as lower priority than any listed tab.
 *
 * Currently internal-only; not user-configurable.
 */
export const TAB_PRIORITY: Record<string, number> = {
  'description-tab': 1,
  'code-editor-tab': 2,
  'test-cases-tab': 3,
  'test-results-tab': 4,
  'notes-tab': 5,
  'attempts-tab': 6,
  'editorial-tab': 7,
}

const UNRANKED = Number.POSITIVE_INFINITY

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LayoutNode = Record<string, any>

function getPriority(tabId: unknown): number {
  if (typeof tabId !== 'string') return UNRANKED
  const value = TAB_PRIORITY[tabId]
  return value ?? UNRANKED
}

/**
 * Within a tabset's children array, find the index of the tab with the lowest
 * priority number. Returns -1 if no children are ranked tabs.
 */
function findHighestPriorityIndex(children: LayoutNode[]): number {
  let bestIdx = -1
  let bestPriority = UNRANKED
  for (let i = 0; i < children.length; i++) {
    const child = children[i]
    if (child?.type !== 'tab') continue
    const p = getPriority(child.id)
    if (p < bestPriority) {
      bestPriority = p
      bestIdx = i
    }
  }
  return bestIdx
}

/** Recursively walk a layout node, applying priority-based selection to every tabset. */
function applyPriorityInNode(node: LayoutNode): void {
  if (node.type === 'tabset' && Array.isArray(node.children)) {
    const idx = findHighestPriorityIndex(node.children)
    if (idx !== -1) {
      node.selected = idx
    }
  }
  if (Array.isArray(node.children)) {
    for (const child of node.children) {
      applyPriorityInNode(child)
    }
  }
}

/**
 * Return a deep-cloned copy of the layout JSON with each tabset's `selected`
 * index updated to point at its highest-priority tab (per `TAB_PRIORITY`).
 *
 * Tabsets containing only unranked tabs are left untouched, as are border
 * panels with no ranked children.
 */
export function selectHighestPriorityTabs(model: IJsonModel): IJsonModel {
  const json = JSON.parse(JSON.stringify(model)) as LayoutNode
  if (json.layout) {
    applyPriorityInNode(json.layout)
  }
  if (Array.isArray(json.borders)) {
    for (const border of json.borders) {
      if (Array.isArray(border.children)) {
        const idx = findHighestPriorityIndex(border.children)
        if (idx !== -1) {
          border.selected = idx
        }
      }
    }
  }
  return json as unknown as IJsonModel
}
